import React, { useState, useEffect } from 'react'
import { apiClient } from '../services/api'

interface LikeButtonProps {
  templateId: number
  initialLikeCount: number
  size?: 'sm' | 'md' | 'lg'
  showCount?: boolean
}

const LIKED_TEMPLATES_KEY = 'likedTemplates'

const getLikedTemplates = (): number[] => {
  try {
    const stored = localStorage.getItem(LIKED_TEMPLATES_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

const saveLikedTemplate = (templateId: number) => {
  const liked = getLikedTemplates()
  if (!liked.includes(templateId)) {
    liked.push(templateId)
    localStorage.setItem(LIKED_TEMPLATES_KEY, JSON.stringify(liked))
  }
}

const removeLikedTemplate = (templateId: number) => {
  const liked = getLikedTemplates()
  const filtered = liked.filter(id => id !== templateId)
  localStorage.setItem(LIKED_TEMPLATES_KEY, JSON.stringify(filtered))
}

export const LikeButton: React.FC<LikeButtonProps> = ({ 
  templateId, 
  initialLikeCount,
  size = 'md',
  showCount = true
}) => {
  const [likeCount, setLikeCount] = useState(initialLikeCount)
  const [isLiked, setIsLiked] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // Check if already liked on mount and sync like count
  useEffect(() => {
    const likedTemplates = getLikedTemplates()
    setIsLiked(likedTemplates.includes(templateId))
    setLikeCount(initialLikeCount)
  }, [templateId, initialLikeCount])

  const handleLike = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    setIsLoading(true)
    try {
      if (isLiked) {
        // Unlike
        const result = await apiClient.unlikeTemplate(templateId)
        setLikeCount(result.like_count)
        setIsLiked(false)
        removeLikedTemplate(templateId)
      } else {
        // Like
        const result = await apiClient.likeTemplate(templateId)
        setLikeCount(result.like_count)
        setIsLiked(true)
        saveLikedTemplate(templateId)
      }
    } catch (error) {
      console.error('Error toggling like:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const sizeClasses = {
    sm: 'text-xs px-2 py-1',
    md: 'text-sm px-3 py-1.5',
    lg: 'text-base px-4 py-2'
  }

  const iconSize = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  }

  return (
    <button
      onClick={handleLike}
      disabled={isLoading}
      className={`
        inline-flex items-center gap-1.5 rounded-lg font-medium transition-all
        ${sizeClasses[size]}
        ${isLiked 
          ? 'bg-red-100 text-red-700 hover:bg-red-200' 
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }
        ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
    >
      <svg 
        className={`${iconSize[size]} transition-transform ${isLiked ? 'scale-110' : ''}`}
        fill={isLiked ? 'currentColor' : 'none'}
        stroke="currentColor" 
        viewBox="0 0 24 24"
      >
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={2} 
          d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" 
        />
      </svg>
      {showCount && <span>{likeCount}</span>}
    </button>
  )
}

